// Placeholder organization data (will be replaced by actual API when backend is ready)
const PLACEHOLDER_ORGANIZATIONS = {
  rentalCarVendors: [
    { id: '1', name: 'Enterprise Rent-A-Car' },
    { id: '2', name: 'Hertz' },
    { id: '3', name: 'Budget' },
  ],
  hotelVendors: [
    { id: '4', name: 'Marriott' },
    { id: '5', name: 'Hilton' },
    { id: '6', name: 'Hyatt' },
  ],
  airlineVendors: [
    { id: '7', name: 'United Airlines' },
    { id: '8', name: 'Delta Airlines' },
    { id: '9', name: 'American Airlines' },
  ],
  otherVendors: [
    { id: '10', name: 'Office Supplies Inc' },
    { id: '11', name: 'Tech Equipment Co' },
    { id: '12', name: 'Conference Services' },
  ],
};

export const tarFormSchema = {
  api: {
    submit: 'tars',
  },
  payload: {
    includeRawFormData: true,
    casts: {
      formVersionNumber: 'int',
      clinx002Value: 'number',
      cumulativeAmountBilled: 'number',
      currentCLINX002Balance: 'number',
      newCLINX002Balance: 'number',
      gaCost: 'number',
      indirectHandlingCost: 'number',
      subtotal: 'number',
      totalNTETripCost: 'number',
    },
    // Transform itineraries and estimates from form data to API structure
    transform: (data) => {
      const transformed = { ...data };
      const tarNumber = data.tarNumber || 'TAR-UNKNOWN';
      
      // Build itineraries array from itinerary form data
      if (Array.isArray(data.itinerary)) {
        transformed.itineraries = data.itinerary
          .filter(traveler => !traveler.__placeholder && (traveler.contactId || traveler.travelerId))
          .map((traveler, index) => {
            const itineraryNumber = String(index + 1).padStart(3, '0');
            const itinerary = {
              name: `${tarNumber}-${itineraryNumber}`,
              travelerId: traveler.contactId || traveler.travelerId,
              hotelId: '',
              rentalId: '',
              airlineId: '',
              legs: [],
            };
            
            // Extract vendor IDs from expenses
            if (Array.isArray(traveler.expenses)) {
              traveler.expenses.forEach(exp => {
                if (exp.type === 'car' && exp.expense) itinerary.rentalId = String(exp.expense);
                if (exp.type === 'hotel' && exp.expense) itinerary.hotelId = String(exp.expense);
                if (exp.type === 'airfare' && exp.expense) itinerary.airlineId = String(exp.expense);
              });
            }
            
            // Build legs array
            if (Array.isArray(traveler.legs)) {
              itinerary.legs = traveler.legs
                .filter(leg => !leg.__placeholder && leg.originLocationId && leg.destinationLocationId)
                .map((leg, legIndex) => ({
                  itineraryLeg: legIndex + 1,
                  originLocationId: leg.originLocationId,
                  departure: leg.departure ? (typeof leg.departure === 'string' ? leg.departure : leg.departure.toISOString()) : null,
                  destinationLocationId: leg.destinationLocationId,
                  arrival: leg.arrival ? (typeof leg.arrival === 'string' ? leg.arrival : leg.arrival.toISOString()) : null,
                }));
            }
            
            return itinerary;
          });
        
        // Remove the form-specific itinerary field
        delete transformed.itinerary;
      }
      
      // Build estimates array - one estimate per itinerary with line items from expenses
      if (Array.isArray(data.itinerary)) {
        transformed.estimates = [];
        data.itinerary
          .filter(traveler => !traveler.__placeholder && (traveler.contactId || traveler.travelerId))
          .forEach((traveler, travelerIndex) => {
            if (!Array.isArray(traveler.expenses)) return;
            
            const estimateNumber = String(travelerIndex + 1).padStart(3, '0');
            const lineItems = [];
            let lineItemCounter = 1;
            
            traveler.expenses
              .filter(exp => !exp.__placeholder)
              .forEach((exp) => {
                const days = parseInt(exp.numberOfDays) || 0;
                const unitCost = parseFloat(exp.itemUnitCost) || 0;
                const totalCost = parseFloat(exp.totalEstimatedCost) || 0;
                
                if (exp.type === 'perDiem' && unitCost > 0 && days > 0) {
                  lineItems.push({
                    lineItem: lineItemCounter++,
                    description: 'PER DIEM',
                    unitCost,
                    days,
                    totalLineItemEstimate: parseFloat((unitCost * days).toFixed(2)),
                  });
                } else if (exp.type === 'hotel' && unitCost > 0 && days > 0) {
                  lineItems.push({
                    lineItem: lineItemCounter++,
                    description: 'HOTEL',
                    unitCost,
                    days,
                    totalLineItemEstimate: parseFloat((unitCost * days).toFixed(2)),
                  });
                } else if (exp.type === 'car' && unitCost > 0 && days > 0) {
                  lineItems.push({
                    lineItem: lineItemCounter++,
                    description: 'CAR RENTAL',
                    unitCost,
                    days,
                    totalLineItemEstimate: parseFloat((unitCost * days).toFixed(2)),
                  });
                } else if (exp.type === 'airfare' && totalCost > 0) {
                  lineItems.push({
                    lineItem: lineItemCounter++,
                    description: 'AIRFARE',
                    unitCost: totalCost,
                    days: 1,
                    totalLineItemEstimate: totalCost,
                  });
                } else if (exp.type === 'other') {
                  const quantity = parseInt(exp.numberOfDays) || 0;
                  if (unitCost > 0 && quantity > 0) {
                    lineItems.push({
                      lineItem: lineItemCounter++,
                      description: 'OTHER',
                      unitCost,
                      days: quantity,
                      totalLineItemEstimate: parseFloat((unitCost * quantity).toFixed(2)),
                    });
                  }
                }
              });
            
            if (lineItems.length > 0) {
              transformed.estimates.push({
                estimateType: 'TAREstimate',
                objectName: `${tarNumber}-${estimateNumber}`,
                lineItems,
                totalEstimate: lineItems.reduce((sum, item) => sum + item.totalLineItemEstimate, 0),
              });
            }
          });
      }
      
      return transformed;
    },
    allow: [
      'associatedLineOfAccounting',
      'awardPIID',
      'clientName',
      'clinx002Value',
      'contractorName',
      'cumulativeAmountBilled',
      'currentCLINX002Balance',
      'estimates',
      'formId',
      'formVersionNumber',
      'fromContactId',
      'gaCost',
      'indirectHandlingCost',
      'itineraries',
      'newCLINX002Balance',
      'projectId',
      'projectInteragencyAgreement',
      'projectName',
      'purposeJustification',
      'rawFormData',
      'subject',
      'subtotal',
      'tarContactId',
      'tarDate',
      'tarNumber',
      'throughContactId',
      'toContactId',
      'totalNTETripCost',
      'travelDate',
      'description',
      'dueDate',
    ],
  },
  blocks: [
    // Hidden values from forms API
    {
      type: 'hiddenApi',
      route: 'forms',
      filter: { formType: 'Trip Authorization Request (TAR) Form' },
      map: {
        formId: 'formId',
        formVersionNumber: 'versionNumber',
      },
    },

    { type: 'heading', label: 'Travel Authorization Request (TAR)' },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'tarNumber', label: 'TAR Number', placeholder: 'TAR-2025-001', required: true, flex: '1 1 300px' },
        { type: 'date', name: 'dueDate', label: 'Due Date', required: false, payloadTransform: (v) => (v && v !== '') ? v : undefined, flex: '0 0 200px' },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'description', label: 'Description', required: false, multiline: true, minRows: 3 },
      ],
    },
    {
      type: 'columnLayout',
      columns: [
        {
          width: '66.67%',
          divider: false,
          fields: [
            {
              type: 'group',
              fields: [
                { type: 'date', name: 'tarDate', label: 'Date', required: true, flex: '1 1 300px' },
                { type: 'date', name: 'travelDate', label: 'Travel Date', required: true, flex: '1 1 300px' },
              ],
            },
            {
              type: 'group',
              fields: [
                { type: 'text', name: 'contractorName', label: 'Contractor Name', placeholder: 'MindPoint Group LLC', required: true, flex: '1 1 300px' },
                { type: 'text', name: 'clientName', label: 'Client Name', placeholder: 'United States Southern Command (USSOUTHCOM)', required: true, flex: '1 1 300px' },
              ],
            },
            {
              type: 'group',
              fields: [
                {
                  type: 'autocomplete',
                  name: 'projectId',
                  label: 'Project Name',
                  required: true,
                  placeholder: 'Search project',
                  loadFromApiRoute: 'projects',
                  loadFromApiField: 'projects',
                  optionLabelKey: 'title',
                  optionValueKey: 'projectId',
                  mapFields: { projectName: 'title', awardPIID: ['awardPIID', 'piid', 'award.piid', 'awardPIIDNumber'] },
                  flex: '1 1 300px'
                },
                { type: 'text', name: 'projectInteragencyAgreement', label: 'Project/Interagency Agreement (IA)', placeholder: 'IA-2025-001', required: true, flex: '1 1 300px' },
              ],
            },
            {
              type: 'group',
              fields: [
                { type: 'text', name: 'associatedLineOfAccounting', label: 'Associated Line of Accounting', placeholder: 'LOA-2025-001', helperText: 'Line Name', required: true, flex: '1 1 300px' },
                { type: 'text', name: 'awardPIID', label: 'Award PIID', required: true, placeholder: 'Enter PIID', helperText: 'PIID Name', flex: '1 1 300px' },
              ],
            },
            {
              type: 'group',
              fields: [
                {
                  type: 'autocomplete',
                  name: 'toContactId',
                  label: 'To',
                  required: true,
                  placeholder: 'Search contact',
                  loadFromApiRoute: 'contacts',
                  optionLabelKey: 'name',
                  optionValueKey: 'contactId',
                  mapFields: { toContactName: 'name' },
                  helperText: "First & Last, AAS Contracting Officer's Representative (COR)",
                  flex: '1 1 300px'
                },
                { type: 'text', name: 'subject', label: 'Subject', placeholder: 'Subject', helperText: 'Insert Subject', required: true, flex: '1 1 300px' },
              ],
            },
            {
              type: 'group',
              fields: [
                {
                  type: 'autocomplete',
                  name: 'throughContactId',
                  label: 'Through',
                  required: true,
                  placeholder: 'Search contact',
                  loadFromApiRoute: 'contacts',
                  optionLabelKey: 'name',
                  optionValueKey: 'contactId',
                  mapFields: { throughContactName: 'name' },
                  helperText: 'Insert First & Last, Insert Organization, Technical Point of Contact (TPOC)',
                  flex: '1 1 300px'
                },
                {
                  type: 'autocomplete',
                  name: 'fromContactId',
                  label: 'From',
                  required: true,
                  placeholder: 'Search contact',
                  loadFromApiRoute: 'contacts',
                  optionLabelKey: 'name',
                  optionValueKey: 'contactId',
                  mapFields: { fromContactName: 'name' },
                  helperText: 'Insert First & Last (Requestor)',
                  flex: '1 1 300px'
                },
              ],
            },
          ],
        },
        {
          width: '33.33%',
          label: 'Last Invoice Submitted:',
          divider: false,
          fields: [
            { type: 'text', name: 'clinx002Value', label: 'CLIN X002 Value', placeholder: '0.00', required: true, dollars: true },
            { type: 'text', name: 'cumulativeAmountBilled', label: 'Cumulative Amount Billed', placeholder: '0.00', required: true, dollars: true },
            { type: 'text', name: 'currentCLINX002Balance', label: 'Current CLIN X002 Balance', placeholder: '0.00', readOnly: true, required: true, dollars: true },
            { type: 'text', name: 'tarEstimate', label: 'TAR Estimate', placeholder: '0.00', readOnly: true, required: true, dollars: true },
            { type: 'text', name: 'newCLINX002Balance', label: 'New CLIN X002 Balance', placeholder: '0.00', readOnly: true, required: true, dollars: true },
          ],
        },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'purposeJustification', label: 'Purpose/Justification of Request', placeholder: 'Purpose/Justification of Request', multiline: true, required: true, minRows: 4, flex: '1 1 100%' },
      ],
    },

    // ===== TRAVEL ITINERARY SECTION =====

    {
      type: 'repeatableSection',
      name: 'itinerary',
      label: 'Travel Itinerary',
      addButtonLabel: 'Add Additional Traveler',
      removeButtonLabel: 'Remove Traveler',
      minItems: 1,
      fields: [
        // Row 1: Traveler Name (full width) — use Autocomplete like TR
        {
          type: 'autocomplete',
          name: 'contactId',
          label: 'Traveler Name',
          required: true,
          placeholder: 'First & Last',
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          mapFields: { travelerName: 'name' },
          flex: '1 1 100%'
        },

        // ===== NESTED TRAVEL LEGS =====
        // Supports multiple legs of travel per traveler (e.g., City A → City B → City C)
        {
          type: 'repeatableSection',
          name: 'legs',
          label: 'Travel Legs',
          addButtonLabel: 'Add Connection/Layover',
          removeButtonLabel: 'Remove Leg',
          minItems: 1,
          helperText: 'Add each leg of travel (e.g., departure city → connecting city → final destination)',
          fields: [
            // Origin location, departure date/time
            {
              type: 'group',
              fields: [
                {
                  type: 'autocomplete',
                  name: 'originLocationId',
                  label: 'Origin',
                  required: true,
                  placeholder: 'Search location',
                  loadFromApiRoute: 'locations',
                  optionLabelKey: 'name',
                  optionValueKey: 'locationId',
                  flex: '1 1 300px'
                },
                { type: 'datetime', name: 'departure', label: 'Departure', required: true, flex: '1 1 300px' },
              ]
            },
            // Destination location, arrival date/time
            {
              type: 'group',
              fields: [
                {
                  type: 'autocomplete',
                  name: 'destinationLocationId',
                  label: 'Destination',
                  required: true,
                  placeholder: 'Search location',
                  loadFromApiRoute: 'locations',
                  optionLabelKey: 'name',
                  optionValueKey: 'locationId',
                  flex: '1 1 300px'
                },
                { type: 'datetime', name: 'arrival', label: 'Arrival', required: true, flex: '1 1 300px' },
              ]
            },
          ]
        },

        // ===== NESTED TRAVEL EXPENSES FOR THIS TRAVELER =====
        {
          type: 'expenseRows',
          name: 'expenses',
          label: 'Travel Expenses (CLIN X002)',
          rows: [
            {
              type: 'perDiem',
              label: 'Per Diem',
              icon: 'perDiem',
              fields: ['numberOfDays', 'itemUnitCost', 'totalEstimatedCost'],
              fixed: true
            },
            {
              type: 'hotel',
              label: 'Hotel',
              icon: 'hotel',
              fields: ['expense', 'numberOfDays', 'itemUnitCost', 'totalEstimatedCost'],
              fixed: true
            },
            {
              type: 'car',
              label: 'Rental Car',
              icon: 'car',
              fields: ['expense', 'numberOfDays', 'itemUnitCost', 'totalEstimatedCost'],
              fixed: true
            },
            {
              type: 'airfare',
              label: 'Airfare',
              icon: 'airfare',
              fields: ['expense', 'totalEstimatedCost'],
              fixed: true
            },
            {
              type: 'other',
              label: 'Other',
              icon: 'other',
              fields: ['expense', 'numberOfDays', 'itemUnitCost', 'totalEstimatedCost'],
              quantityLabel: 'Quantity',
              fixed: false
            },
          ],
          fieldLabels: {
            expense: 'Expense',
            perDiem: '$ Per diem',
            numberOfDays: 'Number of Days',
            itemUnitCost: 'Item/Unit Cost',
            totalEstimatedCost: '$ Total',
          }
        },
      ],
    },

    // ===== COST SUMMARY SECTION =====
    {
      type: 'columnLayout',
      columns: [
        {
          width: '50%',
          fields: [
            { type: 'text', name: 'subtotal', label: 'Subtotal Amount', placeholder: '0.00', readOnly: true, required: true, dollars: true },
            { type: 'text', name: 'indirectHandlingCost', label: 'Indirect Handling Cost', placeholder: '0.00', required: true, dollars: true },
          ],
        },
        {
          width: '50%',
          fields: [
            { type: 'text', name: 'gaCost', label: 'General & Administrative Expenses', placeholder: '0.00', required: true, dollars: true },
            { type: 'text', name: 'totalNTETripCost', label: 'Total NTE Trip Cost', placeholder: '0.00', readOnly: true, required: true, dollars: true },
          ],
        },
      ],
    },

    // ===== AUTHORIZATION SECTION =====
    { type: 'heading', label: 'Authorization', variant: 'h6', divider: false },

    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'tarContactId',
          label: 'Point of Contact (POC)',
          required: true,
          placeholder: 'First & Last',
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          flex: '0 1 300px'
        },
      ],
    },
  ],
};
